import axios from 'axios';

import { mia } from '#services/http-clients.ts';

interface ListRebookingCandidatesParams {
  orgId: number;
}

export type RebookingCandidateStatus =
  | 'needs_outreach'
  | 'contacted'
  | 'waitlisted'
  | 'staff_follow_up';

export interface RebookingCandidate {
  appointmentId: number;
  patientId: number;
  patientFirstName: string | null;
  patientLastName: string | null;
  providerFirstName: string | null;
  providerLastName: string | null;
  appointmentTime: string;
  eventAt: string;
  patientPhone: string | null;
  patientEmail: string | null;
  status: RebookingCandidateStatus;
}

interface ListRebookingCandidatesResponse {
  candidates: RebookingCandidate[];
}

/** Lists each patient who needs rebooking once, with their current workflow status. */
export function listRebookingCandidates(
  params: ListRebookingCandidatesParams
): Promise<ListRebookingCandidatesResponse> {
  return mia
    .get<ListRebookingCandidatesResponse>('/mia/rebooking/candidates', { params })
    .then((response) => response.data);
}

export interface AskAboutRebookingRequest {
  orgId: number;
  appointmentIds: number[];
}

interface RebookingDispatchResult {
  channel: MiaMessageChannel;
  conversationMessageId: number;
  externalMessageId?: string;
}

interface RebookingSkippedChannel {
  channel: MiaMessageChannel;
  reason:
    | 'rate_limited'
    | 'channel_disabled'
    | 'copy_generation_failed'
    | 'dispatch_failed'
    | 'unexpected_error';
}

interface RebookingSentEntry {
  appointmentId: number;
  patientId: number;
  conversationId: number;
  dispatched: RebookingDispatchResult[];
  // TODO(multi-channel): non-empty on a partial send: the server dispatches on every reachable
  // channel, so an appointment can land in `sent` with SMS dispatched and email skipped
  // (channel_disabled while MIA_EMAIL_ENABLED is off, or a per-channel failure). The UI currently
  // only counts sent appointments; surface these when it goes multi-channel.
  skippedChannels: RebookingSkippedChannel[];
}

interface RebookingFailedEntry {
  appointmentId: number;
  patientId: number;
  reason: string;
  skippedChannels: RebookingSkippedChannel[];
}

interface RebookingSkippedEntry {
  appointmentId: number;
  patientId: number;
  reason: 'no_contact_method' | 'all_channels_skipped';
  skippedChannels: RebookingSkippedChannel[];
}

export interface AskAboutRebookingResponse {
  sent: RebookingSentEntry[];
  failed: RebookingFailedEntry[];
  skipped: RebookingSkippedEntry[];
}

/**
 * Triggers Mia's rebooking outreach after a canceled or missed appointment.
 * TODO(multi-channel): the server dispatches on every reachable channel (SMS, and email once
 * MIA_EMAIL_ENABLED is on) — the SMS-only framing is this UI's gate, not the endpoint's (see
 * isSmsReachableRebookingRow in Readiness.tsx).
 */
export function askAboutRebooking(
  request: AskAboutRebookingRequest
): Promise<AskAboutRebookingResponse> {
  return mia
    .post<AskAboutRebookingResponse>('/mia/rebooking/ask', request)
    .then((response) => response.data);
}

export type MiaMessageChannel = 'email' | 'sms';

export type UnconfirmedRowStatus = 'not_contacted' | 'awaiting_confirmation' | 'confirmed';

interface GetConfirmationWorklistParams {
  orgId: number;
  /** How many days of upcoming appointments to include. Server default is 2, max is 14. */
  daysAhead?: number;
}

/**
 * Which deployed model scores an org's appointments. Per-org homogeneous and derived server-side
 * from no-show-cohort membership — a read-side property, never a settable knob. Mirrors
 * `riskKindSchema` in care-coordinator-agent's `libs/shared`, and keys all kind-aware briefing copy.
 */
export type RiskKind = 'no_show' | 'late_cancel';

/**
 * Raw no-show risk factors for the row's patient, aggregated by the effective (household)
 * patient. All four fields share one server-side lookback window (full history by default), so
 * `isNewPatient` means zero past appointments in the same scope the counts cover. Raw factors,
 * not a computed risk score — staff interpret them.
 */
interface UnconfirmedRowRiskFactors {
  noShowCount: number;
  lateCancelCount: number;
  totalAppointmentsInWindow: number;
  isNewPatient: boolean;
}

/**
 * Mirrors `confirmationWorklistRowSchema` in care-coordinator-agent's `libs/shared`. Every field
 * is always present, `null` when absent. Datetime fields are ISO-8601 strings over the REST
 * mirror (they are `Date`s only over tRPC + superjson, which we don't use). `preferredChannel` is
 * the server-side (SMS-first) prediction of what a send would pick. Name fields have no length
 * floor — a provider with blank names comes through as an empty `providerName` string.
 */
interface UnconfirmedRowBase {
  appointmentId: number;
  patientId: number;
  patientFirstName: string | null;
  patientLastName: string | null;
  appointmentStartsAt: string;
  providerName: string;
  locationName: string | null;
  preferredChannel: MiaMessageChannel | null;
  appointmentTypeId: number;
  appointmentTypeName: string;
  riskFactors: UnconfirmedRowRiskFactors;
  /**
   * The deployed model's predicted probability for the org's risk kind. Only the field matching the
   * envelope's `riskKind` is ever populated; the other stays null. Both are null when the
   * appointment is unscored — BigQuery unconfigured, the prediction missing, or the server's read
   * failed and degraded — so a null is "unknown risk", never "low risk".
   */
  predictedNoShowProbability: number | null;
  predictedLateCancelProbability: number | null;
}

interface NotContactedUnconfirmedRow extends UnconfirmedRowBase {
  status: 'not_contacted';
  lastContactedAt: null;
  contactCount: 0;
  conversationId: null;
  confirmedAt: null;
}

interface AwaitingConfirmationUnconfirmedRow extends UnconfirmedRowBase {
  status: 'awaiting_confirmation';
  lastContactedAt: string;
  contactCount: number;
  conversationId: number;
  confirmedAt: null;
}

interface ConfirmedUnconfirmedRow extends UnconfirmedRowBase {
  status: 'confirmed';
  lastContactedAt: string;
  contactCount: number;
  conversationId: number;
  confirmedAt: string;
}

/**
 * A discriminated union the server's schema doesn't declare but its implementation guarantees:
 * the worklist only emits rows that are unconfirmed OR have outreach (the union predicate in
 * `reminder-repository.getConfirmationWorklist`), and `deriveConfirmationStatus` in
 * `reminder-service` maps outreach + confirmation onto `status` — together making the
 * outreach/confirmation fields non-null exactly as each variant states. If the server ever
 * relaxes that predicate, these variants — not just the flat field types — must be revisited.
 */
export type UnconfirmedRow =
  | NotContactedUnconfirmedRow
  | AwaitingConfirmationUnconfirmedRow
  | ConfirmedUnconfirmedRow;

export interface GetConfirmationWorklistResponse {
  rows: UnconfirmedRow[];
  riskKind: RiskKind;
}

export function getConfirmationWorklist(
  params: GetConfirmationWorklistParams
): Promise<GetConfirmationWorklistResponse> {
  return mia
    .get<GetConfirmationWorklistResponse>('/reminders/confirmation-worklist', { params })
    .then((response) => response.data);
}

/** Where a feature's importance was sourced. `unavailable` → `features: []`. */
export type RiskModelScope = 'org' | 'global' | 'unavailable';

/**
 * One side of a feature's comparison. `outcomeRate` is the rate of the org's **risk event**
 * (no-showed / late-canceled), not attendance — never subtract it from 1 unless you deliberately want
 * the positive framing, which the wire permits but the product doesn't use.
 */
interface ComparisonGroup {
  outcomeRate: number;
  sampleSize: number;
}

/**
 * A descriptive comparison for a prediction feature — the "3× more likely" line. `favorableGroup` is
 * the feature's favorable side (e.g. patients with no prior no-shows), `referenceGroup` the rest.
 * Mirrors `riskFeatureComparisonSchema` in care-coordinator-agent's `libs/shared`.
 *
 * Exactly one feature per model carries one (the kind's own history input); it is null on every other
 * feature, and on that one whenever the server withholds it — either group under its sample floor, a
 * reference side that isn't actually riskier, or a favorable side with no outcomes at all. Every such
 * guard is server-side, so a null is simply "no line to draw" and the client adds no math of its own.
 */
export interface RiskFeatureComparison {
  favorableGroup: ComparisonGroup;
  referenceGroup: ComparisonGroup;
  /**
   * The relative risk `referenceGroup.outcomeRate / favorableGroup.outcomeRate` — a multiplier, not a
   * fractional increase. Guaranteed finite and strictly greater than 1.
   */
  riskRatio: number;
}

/**
 * One prediction feature. Mirrors `riskModelFeatureSchema` in care-coordinator-agent's
 * `libs/shared`: a model feature mapped to a human label + description, its `scoreShare` (the
 * "38% of the score" chip), and an optional descriptive `comparison`. No high/med/low banding —
 * raw `scoreShare` only.
 */
export interface RiskFeature {
  key: string;
  label: string;
  description: string;
  scoreShare: number;
  comparison: RiskFeatureComparison | null;
}

/**
 * The attendance-risk model explainer for an org. Mirrors `getRiskModelExplainerOutputSchema` in
 * care-coordinator-agent's `libs/shared`. `features` is ordered by `scoreShare` desc; the modal
 * renders only `features[0]` (the strongest predictor) — the full grid stays a deferred UI.
 *
 * `riskKind` names the outcome the comparison line reports ("more likely to no-show again" vs
 * "…cancel late again"), so it does reach the wording here — unlike the briefing and bucket copy,
 * which stay on the kind-agnostic "attendance risk" umbrella.
 */
export interface RiskModelExplainer {
  riskKind: RiskKind;
  scope: RiskModelScope;
  features: RiskFeature[];
}

interface GetRiskModelExplainerParams {
  orgId: number;
}

/**
 * Explains the org's attendance-risk model. `riskKind` is derived server-side from cohort
 * membership, so the caller only names the org.
 */
export function getRiskModelExplainer(
  params: GetRiskModelExplainerParams
): Promise<RiskModelExplainer> {
  return mia.get<RiskModelExplainer>('/risk-model', { params }).then((response) => response.data);
}

/**
 * Where an org's `riskThreshold` came from. `recommended` is computed from the org's own
 * predicted-risk distribution; `fallback` is one global constant standing in for orgs that have no
 * such value yet, which the client may replace with its own estimate. Mirrors
 * `thresholdSourceSchema` in care-coordinator-agent's `libs/shared`.
 */
export type ThresholdSource = 'saved' | 'recommended' | 'fallback';

/**
 * An org's resolved Mia config. Mirrors `orgConfigSchema` in care-coordinator-agent's `libs/shared`;
 * `riskThreshold` is the predicted-probability floor (0..1) at or above which the daily briefing
 * flags an appointment. Only a `saved` source implies a persisted row, so the audit fields are null
 * for the other two. `updatedAt` is an ISO-8601 string over the REST mirror.
 */
export interface MiaOrgConfig {
  orgId: number;
  riskThreshold: number;
  thresholdSource: ThresholdSource;
  updatedByUserId: number | null;
  updatedAt: string | null;
}

interface GetReadinessConfigParams {
  orgId: number;
}

/** Reads the org's Mia config. Staff-wide — every staff caller sees a threshold-filtered briefing. */
export function getReadinessConfig(params: GetReadinessConfigParams): Promise<MiaOrgConfig> {
  return mia.get<MiaOrgConfig>('/config', { params }).then((response) => response.data);
}

export interface SetReadinessConfigRequest {
  orgId: number;
  riskThreshold: number;
}

/**
 * Persists the org's risk threshold and returns the saved config. Practice-admin gated server-side
 * (`orgAdminProcedure`); the client gates the affordance too, so a non-admin never issues this.
 */
export function setReadinessConfig(request: SetReadinessConfigRequest): Promise<MiaOrgConfig> {
  return mia.post<MiaOrgConfig>('/config', request).then((response) => response.data);
}

export interface SendRemindersRequest {
  orgId: number;
  appointmentIds: number[];
}

interface ReminderSendFailure {
  appointmentId: number;
  patientId: number;
  reason: string;
}

interface ReminderSendSuccess {
  appointmentId: number;
  patientId: number;
  channel: MiaMessageChannel;
  externalMessageId?: string;
  conversationMessageId: number;
  /**
   * The confirmation conversation the send landed on — the same id the worklist row carries, so
   * a sent row can be patched to `awaiting_confirmation` in place instead of refetching.
   */
  conversationId: number;
}

export interface SendRemindersResponse {
  sent: ReminderSendSuccess[];
  failed: ReminderSendFailure[];
  skipped: ReminderSendFailure[];
}

export function sendReminders(request: SendRemindersRequest): Promise<SendRemindersResponse> {
  return mia
    .post<SendRemindersResponse>('/reminders/send', request)
    .then((response) => response.data);
}

export interface PreviewMessageContentRequest {
  kind: 'confirmation' | 'rebooking';
  appointmentId: number;
  channel: MiaMessageChannel;
}

export interface PreviewMessageContentResponse {
  channel: MiaMessageChannel;
  body: string;
  /** Present for email, absent for SMS. */
  subject?: string;
  /** "mastra:vertex" when the LLM wrote the copy, "fallback" for the deterministic template. */
  provider: string;
  model?: string;
}

/** Generates the message Mia would send for an appointment without sending it. */
export function previewMessageContent(
  request: PreviewMessageContentRequest
): Promise<PreviewMessageContentResponse> {
  return mia
    .post<PreviewMessageContentResponse>('/mia/messaging/preview', request)
    .then((response) => response.data);
}

/** Mirrors `sendPreparedInputSchema` in care-coordinator-agent's `libs/shared`. */
export interface SendPreparedRequest {
  // `topic` picks which thread the verbatim copy lands on: the appointment's confirmation thread or
  // its rebooking thread. Both appointment topics carry the same `appointmentId`.
  link: { topic: 'appointment-confirmation' | 'rebooking'; appointmentId: number };
  channel: MiaMessageChannel;
  body: string;
  /** Present for email, omit for SMS. */
  subject?: string;
}

/** Mirrors `sendPreparedOutputSchema` in care-coordinator-agent's `libs/shared`. */
export type SendPreparedResponse =
  | {
      outcome: 'sent';
      conversationId: number;
      conversationMessageId: number;
      externalMessageId?: string;
    }
  | {
      outcome: 'skipped';
      reason:
        | 'invalid_address'
        | 'missing_channel_recipient'
        | 'rate_limited'
        | 'conversation_unavailable';
    }
  | { outcome: 'failed'; reason: 'dispatch_failed' };

/**
 * Dispatches verbatim, staff-vetted copy for one appointment — the exact body/subject/channel the
 * staff previewed. Deliberately bypasses suppression (reaches opted-out and hard-bounced patients),
 * but rebooking still enforces its per-appointment cooldown and protects human-owned/closed
 * threads. The suppression override is audited server-side as `suppressionOverridden`. Requires a
 * staff-plus role server-side.
 */
export function sendPrepared(request: SendPreparedRequest): Promise<SendPreparedResponse> {
  return mia
    .post<SendPreparedResponse>('/mia/messaging/send', request)
    .then((response) => response.data);
}

interface ListOfferableSlotsParams {
  orgId: number;
}

type SlotFillStatus = 'open' | 'claimed' | 'filled' | 'expired';
type SlotOfferStatus = 'pending' | 'offered' | 'claimed' | 'declined' | 'lost' | 'expired';

interface SlotOfferState {
  patientId: number;
  patientFirstName: string | null;
  patientLastName: string | null;
  status: SlotOfferStatus;
  offeredAt: string | null;
}

interface SlotFillState {
  appointmentId: number;
  slotFillId: number;
  status: SlotFillStatus;
  offers: SlotOfferState[];
}

/**
 * One Offerable Slot: a slot-fill-eligible late-canceled appointment. `slotFillState` is null
 * until a blast has been triggered for it — the UI shows the offer button in that state, a state
 * chip once it's set.
 */
export interface OfferableSlot {
  appointmentId: number;
  providerFirstName: string | null;
  providerLastName: string | null;
  slotStartsAt: string;
  slotEndsAt: string;
  canceledAt: string;
  candidateCount: number;
  slotFillState: SlotFillState | null;
}

interface ListOfferableSlotsResponse {
  enabled: boolean;
  offerBatchSize: number;
  slots: OfferableSlot[];
}

/** Mirrors `listOfferableSlotsOutputSchema` in care-coordinator-agent's `libs/shared`. */
export function listOfferableSlots(
  params: ListOfferableSlotsParams
): Promise<ListOfferableSlotsResponse> {
  return mia
    .get<ListOfferableSlotsResponse>('/mia/slot-fill/offerable', { params })
    .then((response) => response.data);
}

interface OfferSlotToWaitlistRequest {
  appointmentId: number;
}

interface OfferedCandidate {
  patientId: number;
  conversationId: number;
  conversationMessageId: number;
}

interface SkippedCandidate {
  patientId: number;
  reason: string;
}

export interface OfferSlotToWaitlistResponse {
  slotFillId: number;
  offered: OfferedCandidate[];
  skipped: SkippedCandidate[];
}

/** Triggers the Slot-Fill Outreach blast: Mia offers the freed slot to its waitlist candidates. */
export function offerSlotToWaitlist(
  request: OfferSlotToWaitlistRequest
): Promise<OfferSlotToWaitlistResponse> {
  return mia
    .post<OfferSlotToWaitlistResponse>('/mia/slot-fill/offer', request)
    .then((response) => response.data);
}

interface ApproveSlotFillRequest {
  slotFillId: number;
}

/**
 * Why a claimed slot couldn't be booked. Mirrors `bookingFailureReasonSchema` in
 * care-coordinator-agent's `libs/shared`.
 */
export type BookingFailureReason =
  | 'slot_gone'
  | 'type_misfit'
  | 'self_booking_disabled'
  | 'api_error';

/**
 * Mirrors `approveSlotFillOutputSchema` in care-coordinator-agent's `libs/shared`. A failed
 * booking changes nothing server-side — the slot stays claimed so staff can retry or release.
 */
export type ApproveSlotFillResponse =
  | { booked: true; appointmentId: number }
  | { booked: false; reason: BookingFailureReason };

/** Approves a claimed slot: Mia books the winner's waitlisted appointment type server-side. */
export function approveSlotFill(request: ApproveSlotFillRequest): Promise<ApproveSlotFillResponse> {
  return mia
    .post<ApproveSlotFillResponse>('/mia/slot-fill/approve', request)
    .then((response) => response.data);
}

interface ReleaseSlotFillClaimRequest {
  slotFillId: number;
}

/** Mirrors `releaseSlotFillClaimOutputSchema` in care-coordinator-agent's `libs/shared`. */
interface ReleaseSlotFillClaimResponse {
  released: boolean;
}

/**
 * The "Reject" action: releases the claim so the slot is back in play. Mia notifies the
 * released patient server-side.
 */
export function releaseSlotFillClaim(
  request: ReleaseSlotFillClaimRequest
): Promise<ReleaseSlotFillClaimResponse> {
  return mia
    .post<ReleaseSlotFillClaimResponse>('/mia/slot-fill/release', request)
    .then((response) => response.data);
}

// Rejection codes SlotFillService can throw when a race changes the slot's eligibility between
// the read and the click (org disabled/reconfigured mid-session, the cancellation ages out of the
// late-cancel window, the slot starts, or someone else's booking fills it first).
const slotFillErrorMessages: Record<string, string> = {
  slot_fill_disabled: 'Slot-Fill Outreach is turned off for this org.',
  slot_fill_config_invalid: "This org's Slot-Fill configuration is invalid.",
  not_late_cancel: 'This cancellation no longer qualifies as a Late Cancel.',
  slot_in_past: 'This slot has already started.',
  slot_refilled: 'This slot has since been filled — no further offers possible.',
};

// Extracts the typed rejection code Mia's REST mirror carries as the error body's `message`.
// data can be null/undefined on malformed upstream error bodies — guard it so the caller's
// fallback copy runs instead of the extraction itself throwing.
function getMiaErrorCode(error: unknown): string | null {
  if (axios.isAxiosError<{ message?: string } | null>(error) && error.response?.data?.message) {
    return error.response.data.message;
  }

  return null;
}

/** Maps an `offerSlotToWaitlist` rejection to human copy, falling back for unrecognized codes. */
export function getOfferSlotErrorMessage(error: unknown): string {
  const code = getMiaErrorCode(error);
  return (
    (code === null ? undefined : slotFillErrorMessages[code]) ??
    'Could not offer this slot. Please try again later.'
  );
}

// The Approve response's `booked: false` reasons, as staff-facing copy. Each leaves the row
// claimed and actionable — the toast explains why the booking didn't happen, not a state change.
// Exhaustively keyed so a new server-side reason fails the build here instead of silently
// falling through; the mirrored type is trust-not-validation, so unrecognized wire values are
// the mapper's fallback, not this table's concern.
const couldntBookReasonMessages: Record<BookingFailureReason, string> = {
  slot_gone: 'This slot is no longer open — it may have been rebooked.',
  type_misfit: "The patient's waitlisted appointment type doesn't fit this slot.",
  self_booking_disabled:
    "This organization has patient self-booking disabled, so Coordinate couldn't book it.",
  api_error: 'Mend could not complete the booking. Please try again.',
};

/** Maps a `booked: false` reason to human copy, falling back for unrecognized wire values. */
export function getCouldntBookMessage(reason: BookingFailureReason): string {
  return (
    (couldntBookReasonMessages as Record<string, string>)[reason] ??
    'Could not book this slot. Please try again.'
  );
}

// Rejection codes for the claim actions (approve/release): the claim changed under the staff
// click, or the record belongs to another org.
const claimActionErrorMessages: Record<string, string> = {
  slot_not_claimed: 'This slot was just updated — refreshing the list.',
  org_mismatch: "You don't have access to act on this slot.",
};

/** Maps an approve/release rejection to human copy, falling back for unrecognized codes. */
export function getClaimActionErrorMessage(error: unknown): string {
  const code = getMiaErrorCode(error);
  return (
    (code === null ? undefined : claimActionErrorMessages[code]) ??
    'Could not complete this action. Please try again later.'
  );
}
